import useSupportLanguage from '@/pages/help/useSupportLanguage.jsx';
// src/pages/login/ForgotPassword.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import Determinate from "@/sot/primitives/Determinate.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { callFunctionRaw } from "@/lib/callFunction.js";

export default function ForgotPassword() {
  useSupportLanguage();
  const nav = useNavigate();
  const { t } = useLanguage();

  const [email, setEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");

  const trimmed = String(email || "").trim();
  const emailLooksOk = /.+@.+\..+/.test(trimmed);

  // tiny determinate progress (no extra hooks)
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    if (!sending) return;
    setProgress(10);
    const t = window.setInterval(() => {
      setProgress((p) => (p >= 90 ? 90 : p + 7));
    }, 180);
    return () => window.clearInterval(t);
  }, [sending]);

  async function handleSend() {
    setError("");
    if (!emailLooksOk) return;

    try {
      setSending(true);

      await callFunctionRaw(
        "account-password-reset",
        {
          action: "request",
          email: trimmed,
          resetBasePath: "/reset-password",
        },
        { timeoutMs: 20000, skipAuth: true },
      );
      setSent(true);
    } catch (e) {
      setError(
        e?.message ||
          t(
            "auth.forgotPassword.sendError",
            "We couldn't send the password reset link. Please try again."
          )
      );
    } finally {
      setSending(false);
      setProgress(100);
      window.setTimeout(() => setProgress(0), 450);
    }
  }

  return (
    <div className="auth-bg min-h-[100svh] grid place-items-center px-4">
      <div className="w-full max-w-[520px] text-center">
        <img
          src="/assets/images/afrimed_logo_stack.svg"
          alt="AfriMed"
          className="mx-auto w-[120px] h-auto mb-6"
          draggable="false"
        />

        <h1 className="text-title-large text-on-surface mb-2">
          {t("auth.forgotPassword.title", "Reset your password")}
        </h1>
        <p className="text-body-medium text-on-surface-var mb-6">
          <span className="block">
            {t(
              "auth.forgotPassword.introLine1",
              "Enter your email and we’ll send you a secure"
            )}
          </span>
          <span className="block">
            {t(
              "auth.forgotPassword.introLine2",
              "link to create a new password."
            )}
          </span>
        </p>

        {!sent ? (
          <div className="mt-4 space-y-4 text-left">
            <div className="mx-auto w-full max-w-[375px]">
              <TextField56
                label={t("auth.forgotPassword.email", "Email")}
                value={email}
                onChange={(v) =>
                  setEmail(typeof v === "string" ? v : v?.target?.value ?? "")
                }
                placeholder={t(
                  "auth.forgotPassword.emailPlaceholder",
                  "you@example.com"
                )}
              />
            </div>

            {/* Subtle CTA */}
            <div className="flex justify-center">
              <button
                type="button"
                disabled={!emailLooksOk || sending}
                onClick={handleSend}
                className="h-9 px-3 rounded-lg text-body-medium font-medium disabled:opacity-60"
                style={{
                  background: "rgba(29,79,215,0.10)",
                  color: "rgb(var(--afrimed-primary))",
                  boxShadow: "0 0 0 1px rgba(29,79,215,0.18) inset",
                }}
              >
                {sending
                  ? t("auth.forgotPassword.sending", "Sending...")
                  : t(
                      "auth.forgotPassword.sendLink",
                      "Send reset link"
                    )}
              </button>
            </div>

            {sending && (
              <div className="mt-2 flex justify-center">
                <Determinate progress={progress || 35} size={32} />
              </div>
            )}

            {!!error && (
              <p className="text-body-medium text-[rgb(var(--afrimed-danger))] text-center">
                {error}
              </p>
            )}

            <div className="pt-2 flex items-center justify-center gap-6">
              <button
                type="button"
                onClick={() => nav("/login")}
                className="text-[rgb(var(--afrimed-primary))] font-medium hover:opacity-90"
              >
                {t("auth.forgotPassword.backToLogin", "Back to login")}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-6">
            <p className="text-body-large text-on-surface">
              {t(
                "auth.forgotPassword.checkEmail",
                "Check your email for a secure password reset link."
              )}
            </p>
            <div className="mt-6 flex items-center justify-center gap-6">
              <button
                type="button"
                onClick={() => nav("/login")}
                className="text-[rgb(var(--afrimed-primary))] font-medium hover:opacity-90"
              >
                {t("auth.forgotPassword.backToLogin", "Back to login")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
